/**

* <newsletter-popup> — timed/scroll-triggered Klaviyo newsletter popup.
 *
 * Loaded ONLY from `sections/section-newsletter_popup.liquid` via
 * `type="module" fetchpriority="low"`. Does not live in global.js.
 *
 * The subscribe form lives in the `_newsletter` theme block, which carries the
 * Klaviyo list id + public API key on `[data-newsletter-settings]`.
 * External triggers use the shared popup contract:
 * `data-action="toggle-popup" aria-controls="newsletter-popup"`.
 */

import { Delegate } from '@theme/delegate';
import { FormValidator } from '@theme/form-validator';
import { globalPopup } from '@theme/popup';
import { scrollListener } from '@theme/scroll-listener';
import { klaviyoSignupProperties } from '@theme/klaviyo-signup-context';

class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegateElement = new Delegate(this.element);
    this.delegateBody = new Delegate(document.body);
    this.cookiePopupDismissed = false;
    this.options = JSON.parse(this.element.getAttribute('data-section-settings') || '{}');
    this.newsletterSettings = this.readNewsletterSettings();
    this.abortController = null;

    this.bindEvents();
    this.setupFormValidator();

    if (this.options.edit_mode) {
      this.options.delay = 0;
      this.openNewsletter();
    } else if (this.options.enable_auto_open !== false) {
      this.checkCookiePopupAndStart();
    }
  }

  disconnectedCallback() {
    // delegateBody lives on document.body, so it must be torn down explicitly or it
    // leaks across Theme Editor section re-renders. delegateElement dies with the element.
    this.delegateBody?.destroy();
    this.abortController?.abort();
    this.clearPendingOpen();
  }

  // Klaviyo list id + public API key ride on the `_newsletter` block's form.
  readNewsletterSettings() {
    const el = this.element.querySelector('[data-newsletter-settings]');
    if (!el) return {};
    try {
      return JSON.parse(el.dataset.newsletterSettings || '{}');
    } catch (error) {
      return {};
    }
  }

  // Don't overlap Shopify's cookie-consent banner: if it is showing, hold the
  // newsletter until the visitor accepts/declines it. No banner (or already
  // dismissed) -> proceed immediately.
  checkCookiePopupAndStart() {
    const cookieBanner = document.querySelector('#shopify-pc__banner');
    const isVisible =
      !!cookieBanner &&
      cookieBanner.style.display !== 'none' &&
      window.getComputedStyle(cookieBanner).display !== 'none';

    if (isVisible) {
      this.waitForCookiePopupDismissal();
    } else {
      this.cookiePopupDismissed = true;
      this.handleOpenOptions();
    }
  }

  waitForCookiePopupDismissal() {
    // Accept and decline both close the banner; either one unblocks the newsletter.
    this.delegateBody.on('click', '#shopify-pc__banner__btn-accept', this.handleCookieButtonClick);
    this.delegateBody.on('click', '#shopify-pc__banner__btn-decline', this.handleCookieButtonClick);
  }

  handleCookieButtonClick = () => {
    // Small delay so the banner finishes animating out before the popup opens.
    window.setTimeout(() => {
      this.cookiePopupDismissed = true;
      this.handleOpenOptions();
    }, 500);
  };

  bindEvents() {
    this.delegateElement.on('popup:before-open', this.handleBeforeOpen);
    this.delegateElement.on('click', '[data-action="close-modal"]', this.closeNewsletter);
    this.delegateElement.on('submit', '[data-newsletter-popup-form]', this.onSubmit.bind(this));
  }

  handleBeforeOpen = () => {
    this.clearPendingOpen();
    this.setStorageItem('popup-seen', 'true');
  };

  clearPendingOpen() {
    if (this.myScrollListener) {
      this.myScrollListener.removeScrollListener();
      this.myScrollListener = null;
    }

    if (this.openTimeout) {
      window.clearTimeout(this.openTimeout);
      this.openTimeout = null;
    }
  }

  setupFormValidator() {
    const form = this.element.querySelector('[data-newsletter-popup-form]');
    if (!form) return;
    const emailInput = form.querySelector('[name="EMAIL"]');
    const errorOutput = this.element.querySelector('[data-newsletter-popup-error]');
    const submitButton = form.querySelector('[data-newsletter-popup-submit]');
    const escapedButtonId =
      submitButton?.id && window.CSS?.escape
        ? window.CSS.escape(submitButton.id)
        : submitButton?.id;
    const buttonSelector = escapedButtonId ? `#${escapedButtonId}` : null;

    this.formElement = form;
    this.emailInput = emailInput;
    this.formValidator = new FormValidator(form, {
      buttonSelector,
      validateOnBlur: true,
      errorOutputSelector: errorOutput,
      showErrorsOnSubmitOnly: false,
    });

    emailInput?.addEventListener('input', () => {
      if (this.formValidator.isFieldValid(emailInput)) {
        this.clearValidationState(emailInput);
        this.showErrorState();
      } else if (this.hasValidationError(emailInput)) {
        this.formValidator.showAllErrors();
        this.showValidationError(emailInput);
      }
    });

    emailInput?.addEventListener('blur', () => {
      if (this.formValidator.isFieldValid(emailInput)) {
        this.clearValidationState(emailInput);
        this.showErrorState();
        return;
      }

      this.formValidator.showAllErrors();
      this.showValidationError(emailInput);
    });
  }

  handleOpenOptions() {
    // Guard: never open while the cookie banner is still up (safety net for any
    // path that reaches here before dismissal).
    if (!this.cookiePopupDismissed) return;

    const seen = this.getStorageItem('popup-seen') === 'true';
    const subscribed = this.getStorageItem('popup-subscribed') === 'true';

    if ((this.options.show_only_once && seen) || subscribed) {
      this.element.style.display = 'none';
      this.element.setAttribute('aria-hidden', 'true');
      this.element.setAttribute('aria-expanded', 'false');
      this.element.setAttribute('inert', '');
      return;
    }

    if (this.options.scroll_amount > 0) {
      this.myScrollListener = scrollListener.addScrollListener(this, this.scrollListener);
    } else {
      this.openNewsletter();
    }
  }

  scrollListener = () => {
    const body = document.body;
    const html = document.documentElement;

    const docHeight = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.clientHeight,
      html.scrollHeight,
      html.offsetHeight
    );

    const percScrolled = (window.scrollY / docHeight) * 100;

    if (percScrolled >= this.options.scroll_amount) {
      this.openNewsletter();
    }
  };

  openNewsletter() {
    this.clearPendingOpen();

    this.openTimeout = window.setTimeout(() => {
      this.openTimeout = null;

      globalPopup.openPopup(this.element);

      this.setStorageItem('popup-seen', 'true');
    }, 1000 * this.options.delay);
  }

  closeNewsletter = () => {
    globalPopup.closePopup(this.element);

    this.resetPopupContentState();
    this.clearPendingOpen();
  };

  async onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    const emailInput = form.querySelector('[name="EMAIL"]');
    const email = emailInput ? emailInput.value.trim() : '';

    if (!this.validatePopupForm(emailInput)) {
      return;
    }

    if (!email) {
      this.showErrorState(
        new Error(emailInput?.validationMessage || 'Please enter your email address.')
      );
      return;
    }

    const { listId, publicApiKey } = this.newsletterSettings || {};
    if (!listId || !publicApiKey) {
      this.showErrorState(new Error('Klaviyo list ID or public API key is missing.'));
      return;
    }

    this.showErrorState();
    this.setSubmitState(true);

    try {
      const response = await this.subscribeToKlaviyo(email);

      // 202 Accepted is the Klaviyo success status; 409 means the profile is
      // already subscribed — from the visitor's point of view that's still a win.
      if (response.ok || response.status === 409) {
        this.setStorageItem('popup-subscribed', 'true');
        this.showSuccessState();
      } else {
        this.showErrorState(new Error('Something went wrong. Please try again.'));
      }
    } catch (error) {
      if (error?.name !== 'AbortError') this.showErrorState(error);
    } finally {
      this.setSubmitState(false);
    }
  }

  subscribeToKlaviyo(email) {
    const { listId, publicApiKey } = this.newsletterSettings || {};

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          // Which market and language the visitor signed up from - the same properties the
          // footer and the notify-me form send, so one segment covers all three.
          profile: {
            data: {
              type: 'profile',
              attributes: { email, properties: klaviyoSignupProperties() },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    };

    const endpoint = `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicApiKey)}`;

    this.abortController?.abort();
    this.abortController = new AbortController();

    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: '2024-06-15',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify(payload),
      keepalive: true,
      signal: this.abortController.signal,
    });
  }

  resetPopupContentState() {
    const initialContent = this.element.querySelector('.newsletter-popup__initial-content');
    const successState = this.element.querySelector('.newsletter-popup__success-state');

    initialContent?.classList.remove('is-hidden');
    successState?.classList.add('is-hidden');
    this.element.classList.remove('newsletter-popup--success');
    this.resetDialogAnnouncement();
    this.showErrorState();
    this.clearValidationState(this.emailInput);
  }

  showSuccessState() {
    const initialContent = this.element.querySelector('.newsletter-popup__initial-content');
    const successState = this.element.querySelector('.newsletter-popup__success-state');
    const successHeading = this.element.querySelector('.newsletter-popup__success-heading');
    const successText = this.element.querySelector('.newsletter-popup__success-text');

    initialContent?.classList.add('is-hidden');
    successState?.classList.remove('is-hidden');
    this.element.classList.add('newsletter-popup--success');

    if (successHeading?.id) {
      this.element.setAttribute('aria-labelledby', successHeading.id);
    }

    if (successText?.id) {
      this.element.setAttribute('aria-describedby', successText.id);
    }

    successHeading?.focus({ preventScroll: true });
  }

  showErrorState(error) {
    const errorWrapper = this.element.querySelector('[data-newsletter-popup-error]');
    if (!errorWrapper) return;
    errorWrapper.textContent = error && error.message ? error.message : '';
  }

  validatePopupForm(emailInput) {
    if (!this.formValidator) return true;

    this.formValidator.markSubmitted();

    if (this.formValidator.showAllErrors()) {
      this.clearValidationState(emailInput);
      return true;
    }

    this.showValidationError(emailInput);
    return false;
  }

  showValidationError(emailInput) {
    if (!emailInput) return;

    emailInput.setAttribute('aria-invalid', 'true');
    emailInput.closest('.field')?.classList.add('field--error');

    const errorOutput = this.element.querySelector('[data-newsletter-popup-error]');
    if (errorOutput?.textContent.trim()) return;

    const message = emailInput.validity.valueMissing
      ? 'Please enter your email address.'
      : emailInput.validationMessage || 'Please enter a valid email address.';

    this.showErrorState(new Error(message));
  }

  clearValidationState(emailInput) {
    if (!emailInput) return;

    emailInput.removeAttribute('aria-invalid');
    emailInput.closest('.field')?.classList.remove('field--error');
  }

  hasValidationError(emailInput) {
    if (!emailInput) return false;
    return (
      emailInput.getAttribute('aria-invalid') === 'true' ||
      emailInput.closest('.field')?.classList.contains('field--error')
    );
  }

  resetDialogAnnouncement() {
    const initialHeading = this.element.querySelector('.newsletter-popup__heading');
    const initialText = this.element.querySelector('.newsletter-popup__text');

    if (initialHeading?.id) {
      this.element.setAttribute('aria-labelledby', initialHeading.id);
    }

    if (initialText?.id) {
      this.element.setAttribute('aria-describedby', initialText.id);
    }
  }

  setSubmitState(isSubmitting) {
    const submitButton = this.element.querySelector('.newsletter-popup__submit');
    if (!submitButton) return;

    submitButton.toggleAttribute('aria-busy', isSubmitting);

    if (isSubmitting) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      return;
    }

    if (this.formValidator) {
      this.formValidator.updateButtonState();
      return;
    }

    submitButton.disabled = false;
    submitButton.setAttribute('aria-disabled', 'false');
  }

  getStorageItem(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  setStorageItem(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Local storage can be unavailable in privacy modes; popup still works.
    }
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
